import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Warehouse, Plus, Pencil, Trash2, Phone, Mail, Save, X, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_PRODUCT = { name: '', price: '' };

export default function Suppliers() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ company_name: '', lastname: '', firstname: '', email: '', phone: '', products: [{ ...EMPTY_PRODUCT }] });
  const [expandedId, setExpandedId] = useState(null);
  const isAdmin = user?.role === 'admin';

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/suppliers`, { withCredentials: true });
      setSuppliers(res.data);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const openCreate = () => {
    setEditId(null);
    setForm({ company_name: '', lastname: '', firstname: '', email: '', phone: '', products: [{ ...EMPTY_PRODUCT }] });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      company_name: s.company_name,
      lastname: s.lastname,
      firstname: s.firstname,
      email: s.email,
      phone: s.phone,
      products: s.products?.length > 0 ? s.products : [{ ...EMPTY_PRODUCT }],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error(t('sup_company_required')); return; }
    try {
      if (editId) {
        await axios.put(`${API}/api/suppliers/${editId}`, form, { withCredentials: true });
        toast.success(t('sup_updated'));
      } else {
        await axios.post(`${API}/api/suppliers`, form, { withCredentials: true });
        toast.success(t('sup_created'));
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('sup_confirm_delete'))) return;
    try {
      await axios.delete(`${API}/api/suppliers/${id}`, { withCredentials: true });
      toast.success(t('sup_deleted'));
      fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updateProduct = (idx, field, value) => {
    setForm(prev => ({ ...prev, products: prev.products.map((p, i) => i === idx ? { ...p, [field]: value } : p) }));
  };
  const addProduct = () => setForm(prev => ({ ...prev, products: [...prev.products, { ...EMPTY_PRODUCT }] }));
  const removeProduct = (idx) => setForm(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== idx) }));

  if (loading) return <Layout><div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Warehouse size={24} className="text-slate-700" />
            <h1 className="text-2xl font-semibold text-slate-900">{t('sup_title')}</h1>
            <Badge className="bg-slate-200 text-slate-700">{suppliers.length}</Badge>
          </div>
          {isAdmin && (
            <Button data-testid="add-supplier-btn" onClick={openCreate} className="gap-1.5">
              <Plus size={16} /> {t('sup_add')}
            </Button>
          )}
        </div>

        {suppliers.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="py-12 text-center text-sm text-slate-500">{t('sup_empty')}</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {suppliers.map(s => (
              <Card key={s.id} className="border-slate-200 hover:shadow-sm transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold flex-shrink-0">
                      {(s.company_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm text-slate-900">{s.company_name}</h3>
                        {s.products?.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{s.products.length} {t('sup_products_count')}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500">{s.firstname} {s.lastname}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {s.email && <span className="flex items-center gap-1"><Mail size={12} /> {s.email}</span>}
                      {s.phone && <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>}
                    </div>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}>
                      <Package size={14} className="mr-1" /> {t('sup_products')}
                    </Button>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => openEdit(s)}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDelete(s.id)}><Trash2 size={14} /></Button>
                      </div>
                    )}
                  </div>
                  {expandedId === s.id && s.products?.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="font-medium">{t('sup_product_name')}</TableHead>
                            <TableHead className="font-medium text-right">{t('sup_product_price')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {s.products.map((p, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm font-medium">{p.name}</TableCell>
                              <TableCell className="text-sm text-right font-bold">{p.price ? Number(p.price).toLocaleString() : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? t('sup_edit') : t('sup_add')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">{t('sup_company')} *</Label>
                <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('sup_lastname')}</Label>
                  <Input value={form.lastname} onChange={e => setForm(f => ({ ...f, lastname: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{t('sup_firstname')}</Label>
                  <Input value={form.firstname} onChange={e => setForm(f => ({ ...f, firstname: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{t('sup_phone')}</Label>
                  <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">{t('sup_products')}</Label>
                  <Button size="sm" variant="outline" className="text-xs gap-1" onClick={addProduct}><Plus size={12} /> {t('sup_add_product')}</Button>
                </div>
                {form.products.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input value={p.name} onChange={e => updateProduct(i, 'name', e.target.value)} placeholder={t('sup_product_name')} className="h-9 text-sm flex-1" />
                    <Input type="number" value={p.price} onChange={e => updateProduct(i, 'price', e.target.value)} placeholder={t('sup_product_price')} className="h-9 text-sm w-32" />
                    {form.products.length > 1 && (
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeProduct(i)}><X size={14} /></Button>
                    )}
                  </div>
                ))}
              </div>
              <Button className="w-full gap-1.5" onClick={handleSave}><Save size={16} /> {editId ? t('sup_save') : t('sup_create')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
